import { z } from "zod";

import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { TaskSource } from "@/src/lib/task-source";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Document } from "@/src/models/document";
import { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Referral } from "@/src/models/documents/referral";
import { File } from "@/src/models/file";
import { Patient } from "@/src/models/patient";
import { OrderWorkflowStage, type PatientOrder } from "@/src/models/patient-order";
import { Section } from "@/src/models/section";

export enum TaskOwnerType {
  USER = "user",
  SYSTEM = "system",
}

export { TaskSource };

export enum TaskType {
  DOC_MANAGEMENT = "documents",
  ELIGIBILITY_CHECK = "eligibility_check",
  PATIENT_LOOKUP = "patient_lookup",
  PRIOR_AUTHORIZATION = "prior_authorization",
  REFERRAL = "referral",
  REFERRAL_AUTHORIZATION = "referral_authorization",
  SCHEDULE = "schedule",
  SCHEDULING_ELIGIBILITY_VERIFICATION = "scheduling_eligibility_verification", // Human verification of the multi-scenario scheduling-eligibility recommendation, spun off when a referral task completes
  VERIFY_APPOINTMENT = "verify_appointment",
  WELCOME_CALL = "welcome_call",
  LEAD_FORM = "lead_form",
  INTAKE = "intake", // Pre-sync task that bundles classify, intake, and other miscellaneous steps like grouping and enrichment
  COMMS_QA = "comms_qa",
  ORDER_QUALIFY = "order_qualify",
  ORDER_PROVIDER_SELECTION = "order_provider_selection",
  ORDER_AUTH_CHECK = "order_auth_check",
  ORDER_AUTH_SUBMIT = "order_auth_submit",
  ORDER_EHR_SYNC = "order_ehr_sync",
}

export const ORDER_TASK_TYPES = [
  TaskType.ORDER_QUALIFY,
  TaskType.ORDER_PROVIDER_SELECTION,
  TaskType.ORDER_AUTH_CHECK,
  TaskType.ORDER_AUTH_SUBMIT,
  TaskType.ORDER_EHR_SYNC,
] as const;

export type OrderTaskType = (typeof ORDER_TASK_TYPES)[number];

export function isOrderTaskType(type: TaskType | string | null | undefined): type is OrderTaskType {
  return ORDER_TASK_TYPES.includes(type as OrderTaskType);
}

export function workflowStageForOrderTaskType(type: OrderTaskType): OrderWorkflowStage {
  switch (type) {
    case TaskType.ORDER_QUALIFY:
      return OrderWorkflowStage.VERIFICATION;
    case TaskType.ORDER_PROVIDER_SELECTION:
      return OrderWorkflowStage.PROVIDER_SELECTION;
    case TaskType.ORDER_AUTH_CHECK:
      return OrderWorkflowStage.AUTHORIZATION;
    case TaskType.ORDER_AUTH_SUBMIT:
    case TaskType.ORDER_EHR_SYNC:
      return OrderWorkflowStage.SUBMISSION;
    default: {
      const _exhaustive: never = type;

      return _exhaustive;
    }
  }
}

export function getTaskTypeDisplayName(taskType: TaskType): string {
  if (taskType === TaskType.COMMS_QA) return "Comms QA";

  if (isOrderTaskType(taskType)) {
    const stepLabel = formatFromUnderscoreToHumanReadable(taskType.replace(/^order_/, ""));

    return `Order - ${stepLabel}`;
  }

  return formatFromUnderscoreToHumanReadable(taskType);
}

export enum TaskStatus {
  AWAITING_INFORMATION = "awaiting_information",
  BACKLOG = "backlog",
  BLOCKED = "blocked",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  DUPLICATE = "duplicate",
  FAILED = "failed",
  NOT_RELEVANT = "not_relevant",
  PAUSED = "paused",
  PROCESSING = "processing",
  REVIEW_NEEDED = "review_needed",
  REVERT = "revert",
  SHADOW = "shadow", // Hidden from all default views and power mode; visible only via explicit status filter
  VERIFICATION_NEEDED = "verification_needed",
}

export enum TaskPriority {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

// Schema for the metadata JSONB column on task
export const taskMetadataSchema = z.object({}).passthrough();
export type TaskMetadata = z.infer<typeof taskMetadataSchema>;

// Active or recoverable task states that can restart their current workflow from scratch.
export const RETRYABLE_STATUSES = [
  TaskStatus.PROCESSING,
  TaskStatus.REVIEW_NEEDED,
  TaskStatus.FAILED,
  TaskStatus.VERIFICATION_NEEDED,
];

// Task types that can be reverted
export const CAN_REVERT_TASK_TYPES = [
  TaskType.PATIENT_LOOKUP,
  TaskType.INTAKE,
  TaskType.ORDER_PROVIDER_SELECTION,
  TaskType.ORDER_AUTH_CHECK,
  TaskType.ORDER_AUTH_SUBMIT,
  TaskType.ORDER_EHR_SYNC,
];

type TaskWorkflowIdentity = {
  type: TaskType | string | null;
  source: TaskSource | string | null;
};

export function hasIntakeAgentWorkflow(task: TaskWorkflowIdentity): boolean {
  return task.type === TaskType.INTAKE && task.source !== TaskSource.SPREADSHEET_INGESTION;
}

export function canRevertTask(task: TaskWorkflowIdentity): boolean {
  if (!CAN_REVERT_TASK_TYPES.includes(task.type as TaskType)) return false;

  return task.type !== TaskType.INTAKE || hasIntakeAgentWorkflow(task);
}

export function statusesNotActionable(task: Partial<Task>) {
  return (
    task.status === TaskStatus.BLOCKED ||
    task.status === TaskStatus.CANCELLED ||
    task.status === TaskStatus.COMPLETED ||
    task.status === TaskStatus.DUPLICATE ||
    task.status === TaskStatus.NOT_RELEVANT ||
    task.status === TaskStatus.PAUSED ||
    task.status === TaskStatus.PROCESSING ||
    task.status === TaskStatus.REVERT ||
    task.status === TaskStatus.SHADOW
  );
}

export function statusesNotSettable(status: TaskStatus): boolean {
  return (
    status === TaskStatus.COMPLETED ||
    status === TaskStatus.PAUSED ||
    status === TaskStatus.PROCESSING ||
    status === TaskStatus.SHADOW
  );
}

// Define completed statuses as an array of TaskStatus values
export const completedStatuses = [
  TaskStatus.BLOCKED,
  TaskStatus.CANCELLED,
  TaskStatus.COMPLETED,
  TaskStatus.DUPLICATE,
  TaskStatus.NOT_RELEVANT,
  TaskStatus.REVERT,
];

// Statuses hidden from all default views regardless of showCompleted; only visible when explicitly selected in the status filter.
export const hideUnlessFilteredStatuses = [TaskStatus.PAUSED, TaskStatus.SHADOW];

export function isCompleted(task: Task) {
  return completedStatuses.includes(task.status);
}

export const taskSchema = z.object({
  displayId: z.string().nullable(), // todo: why is this nullable?
  description: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  status: z.nativeEnum(TaskStatus),
  type: z.nativeEnum(TaskType),
  originalAssignedToUserId: z.string().uuid().nullable(),
  currentAssignedToUserId: z.string().uuid().nullable(),
  assignedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  source: z.nativeEnum(TaskSource),
  priority: z.nativeEnum(TaskPriority),
  previousTaskId: z.string().uuid().nullable(),
  ownerType: z.nativeEnum(TaskOwnerType),
  metadata: taskMetadataSchema.nullable(),
});

export type Task = z.infer<typeof taskSchema> & {
  patient: Patient | null;
  referral: Referral | null;
  files: File[];
  priorAuthorization: PriorAuthorization | null;
  document: Document | null;
  section: Section | null;
  patientOrder: PatientOrder | null;
  metadata: Record<string, unknown> | null;
} & DatabaseDefaults;

// Mapping object for past tense status
export const pastTenseStatus: Record<TaskStatus, string> = {
  [TaskStatus.AWAITING_INFORMATION]: "Document Received",
  [TaskStatus.BACKLOG]: "Backlogged",
  [TaskStatus.BLOCKED]: "Blocked",
  [TaskStatus.CANCELLED]: "Cancelled",
  [TaskStatus.COMPLETED]: "Completed",
  [TaskStatus.DUPLICATE]: "Marked as Duplicate",
  [TaskStatus.FAILED]: "Failed",
  [TaskStatus.NOT_RELEVANT]: "Not Relevant",
  [TaskStatus.PAUSED]: "Paused",
  [TaskStatus.PROCESSING]: "Processed",
  [TaskStatus.REVIEW_NEEDED]: "Human Reviewed",
  [TaskStatus.REVERT]: "Reverted",
  [TaskStatus.SHADOW]: "Shadow",
  [TaskStatus.VERIFICATION_NEEDED]: "Human Verified",
};

export const taskShape: Task = {
  id: "",
  displayId: null,
  description: null,
  tags: [],
  status: TaskStatus.PROCESSING,
  patient: null,
  type: TaskType.INTAKE,
  originalAssignedToUserId: null,
  currentAssignedToUserId: null,
  assignedAt: null,
  groupId: "",
  source: TaskSource.EMAIL_INGESTION,
  priority: TaskPriority.MEDIUM,
  files: [],
  previousTaskId: null,
  ownerType: TaskOwnerType.USER,
  referral: null,
  priorAuthorization: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  completedAt: null,
  document: null,
  section: null,
  patientOrder: null,
  metadata: null,
};
